import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Shield, Loader2, CheckCircle2, AlertTriangle, Ban, XCircle, Info,
} from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Phase 25.4A — Payment Information card for Agent / Agency / Subagency profiles.
 *
 * Visible ONLY to KR SuperAdmin + Accounting users. Renders nothing for
 * other users (the caller decides whether to mount this component at all).
 */
const W9_OPTIONS = [
  { value: 'missing',        label: 'Missing' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'complete',       label: 'Complete' },
  { value: 'rejected',       label: 'Rejected' },
  { value: 'not_required',   label: 'Not Required' },
];
const DD_OPTIONS = [
  { value: 'missing',        label: 'Missing' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'complete',       label: 'Complete' },
  { value: 'rejected',       label: 'Rejected' },
  { value: 'inactive',       label: 'Inactive' },
];
const CHECK_OPTIONS = [
  { value: 'not_available',         label: 'Not Available' },
  { value: 'available',             label: 'Available' },
  { value: 'preferred',             label: 'Preferred' },
  { value: 'temporarily_disabled',  label: 'Temporarily Disabled' },
];
const DELIVERY_OPTIONS = [
  { value: 'mail',           label: 'Mail' },
  { value: 'pickup',         label: 'Pickup' },
  { value: 'hand_delivered', label: 'Hand Delivered' },
  { value: 'other',          label: 'Other' },
];
const METHOD_OPTIONS = [
  { value: 'direct_deposit', label: 'Direct Deposit / ACH' },
  { value: 'check',          label: 'Check' },
  { value: 'no_preference',  label: 'No Preference' },
];

const BADGE_TONE = {
  complete:              'bg-emerald-100 text-emerald-800',
  available:             'bg-emerald-100 text-emerald-800',
  preferred:             'bg-emerald-100 text-emerald-800',
  pending_review:        'bg-amber-100 text-amber-900',
  missing:               'bg-slate-200 text-slate-700',
  not_available:         'bg-slate-200 text-slate-700',
  not_required:          'bg-blue-100 text-blue-800',
  rejected:              'bg-red-100 text-red-800',
  inactive:              'bg-slate-200 text-slate-700',
  temporarily_disabled:  'bg-red-100 text-red-800',
};

export default function PaymentInformationCard({ recipientType, recipientId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [justSavedKey, setJustSavedKey] = useState(null);

  const load = useCallback(async () => {
    if (!recipientType || !recipientId) return;
    setLoading(true);
    try {
      const { data } = await accountingAPI.getPaymentInfo(recipientType, recipientId);
      setInfo(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment info');
    } finally { setLoading(false); }
  }, [recipientType, recipientId]);

  useEffect(() => { load(); }, [load]);

  // Surgical patch after a status change — no full reload.
  const applyPatch = (key, updated) => {
    setInfo(updated);
    setJustSavedKey(key);
    setTimeout(() => setJustSavedKey(null), 1200);
  };

  const saveW9 = async (status) => {
    setSavingKey('w9');
    try {
      const { data } = await accountingAPI.updateW9(recipientType, recipientId, { status });
      applyPatch('w9', data);
      toast.success('W-9 status saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingKey(null); }
  };
  const saveDD = async (status) => {
    setSavingKey('dd');
    try {
      const { data } = await accountingAPI.updateDirectDeposit(recipientType, recipientId, { status });
      applyPatch('dd', data);
      toast.success('Direct Deposit status saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingKey(null); }
  };
  const saveCheck = async (payload) => {
    setSavingKey('check');
    try {
      const { data } = await accountingAPI.updateCheck(recipientType, recipientId, payload);
      applyPatch('check', data);
      toast.success('Check status saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingKey(null); }
  };
  const savePreferred = async (method) => {
    setSavingKey('preferred');
    try {
      const { data } = await accountingAPI.updatePreferredMethod(recipientType, recipientId, method);
      applyPatch('preferred', data);
      toast.success('Preferred method saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingKey(null); }
  };

  if (loading || !info) {
    return (
      <Card><CardContent className="p-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></CardContent></Card>
    );
  }

  const eligible = info.payment_eligibility === 'eligible';

  return (
    <Card data-testid="payment-information-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={15} className="text-slate-700" />
          <CardTitle className="text-base">Payment Information</CardTitle>
          <Badge className="text-[10px] bg-slate-200 text-slate-700">Restricted · KR / Accounting</Badge>
        </div>
        <Badge
          className={`gap-1 ${eligible ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}
          data-testid={`eligibility-badge-${info.payment_eligibility}`}
        >
          {eligible ? <CheckCircle2 size={11} /> : <Ban size={11} />} {eligible ? 'Payment Eligible' : 'Payment Blocked'}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {!eligible && info.blocking_reasons?.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900"
               data-testid="blocking-reasons">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium mb-1">Payment blocked</div>
              <ul className="list-disc ml-4 space-y-0.5">
                {info.blocking_reason_labels.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
          </div>
        )}

        {/* W-9 */}
        <StatusRow
          label="W-9 Status"
          testid="w9-row"
          badge={<Badge className={BADGE_TONE[info.w9_status] || 'bg-slate-200'}>{labelOf(W9_OPTIONS, info.w9_status)}</Badge>}
          value={info.w9_status}
          options={W9_OPTIONS}
          onChange={saveW9}
          saving={savingKey === 'w9'}
          justSaved={justSavedKey === 'w9'}
          meta={info.w9_verified_at ? { at: info.w9_verified_at, by: info.w9_verified_by_name } : null}
        />

        {/* Direct Deposit */}
        <StatusRow
          label="Direct Deposit"
          testid="dd-row"
          badge={<Badge className={BADGE_TONE[info.direct_deposit_status] || 'bg-slate-200'}>{labelOf(DD_OPTIONS, info.direct_deposit_status)}</Badge>}
          value={info.direct_deposit_status}
          options={DD_OPTIONS}
          onChange={saveDD}
          saving={savingKey === 'dd'}
          justSaved={justSavedKey === 'dd'}
          meta={info.direct_deposit_verified_at ? { at: info.direct_deposit_verified_at, by: info.direct_deposit_verified_by_name } : null}
          hint="Do not store full routing / account numbers here — use external secure storage."
        />

        {/* Check */}
        <div className="space-y-2 rounded-md border border-slate-100 p-3" data-testid="check-row">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Check</Label>
              <Badge className={BADGE_TONE[info.check_status] || 'bg-slate-200'}>{labelOf(CHECK_OPTIONS, info.check_status)}</Badge>
              {justSavedKey === 'check' && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-medium">
                  <CheckCircle2 size={10} /> Saved
                </span>
              )}
            </div>
            <Select
              value={info.check_status}
              onValueChange={(v) => saveCheck({ status: v })}
              disabled={savingKey === 'check'}
            >
              <SelectTrigger className="w-52 h-8 text-xs" data-testid="check-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHECK_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Preferred delivery method</Label>
              <Select
                value={info.check_preferred_delivery_method || 'mail'}
                onValueChange={(v) => saveCheck({ status: info.check_status, preferred_delivery_method: v })}
                disabled={savingKey === 'check'}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="check-delivery-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DELIVERY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Checkbox
                id="chk-addr"
                checked={info.check_mailing_address_confirmed}
                onCheckedChange={(v) => saveCheck({
                  status: info.check_status,
                  mailing_address_confirmed: !!v,
                })}
                disabled={savingKey === 'check'}
                data-testid="check-addr-confirm"
              />
              <Label htmlFor="chk-addr" className="text-xs cursor-pointer">
                Mailing address confirmed
              </Label>
            </div>
          </div>
          {info.check_verified_at && (
            <div className="text-[10px] text-slate-500">
              Last verified {(info.check_verified_at || '').slice(0, 10)} · by {info.check_verified_by_name || '—'}
            </div>
          )}
        </div>

        {/* Preferred method */}
        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Preferred Payment Method</Label>
            <Badge className="text-[10px] bg-slate-100 text-slate-700">{labelOf(METHOD_OPTIONS, info.preferred_method)}</Badge>
            {justSavedKey === 'preferred' && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-medium">
                <CheckCircle2 size={10} /> Saved
              </span>
            )}
          </div>
          <Select value={info.preferred_method} onValueChange={savePreferred} disabled={savingKey === 'preferred'}>
            <SelectTrigger className="w-52 h-8 text-xs" data-testid="preferred-method-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METHOD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-start gap-2 text-[11px] text-slate-500 border-t border-slate-100 pt-2">
          <Info size={11} className="mt-0.5 shrink-0" />
          <div>
            The actual payment method used will be confirmed inside each Payment Batch.
            Documents like W-9 forms and voided checks remain in external storage (e.g. HubSpot).
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function labelOf(options, value) {
  return options.find((o) => o.value === value)?.label || value;
}

function StatusRow({ label, testid, badge, value, options, onChange, saving, justSaved, meta, hint }) {
  return (
    <div className="rounded-md border border-slate-100 p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">{label}</Label>
          {badge}
          {justSaved && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-medium">
              <CheckCircle2 size={10} /> Saved
            </span>
          )}
        </div>
        <Select value={value} onValueChange={onChange} disabled={saving}>
          <SelectTrigger className="w-52 h-8 text-xs" data-testid={`${testid}-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {meta && (
        <div className="mt-1 text-[10px] text-slate-500">
          Last verified {(meta.at || '').slice(0, 10)} · by {meta.by || '—'}
        </div>
      )}
      {hint && (
        <div className="mt-1 text-[10px] text-amber-700 italic">{hint}</div>
      )}
    </div>
  );
}
